// Where the learner is in a flow: one dot per step, each a button to its step. The screen decides
// which steps are reachable, so the dots never carry a gating rule of their own. One Tab stop for
// the row; the arrow keys, Home and End walk it.
import "./config";
import { useRef } from "react";
import { View } from "tamagui";
import { Row } from "./layout";

export interface StepDot {
  /** The accessible name: "Stap 2 van 5: Check". */
  label: string;
  /** False when the flow would not let the learner get there yet (an unanswered check before it). */
  reachable: boolean;
}

export interface StepDotsProps {
  steps: readonly StepDot[];
  /** 1-based step in view. */
  current: number;
  label: string;
  /** A reachable dot was chosen: the 1-based step. */
  onSelect: (step: number) => void;
  /** A locked dot was chosen: say why instead of doing nothing. */
  onBlocked?: (() => void) | undefined;
  /** Prefix of the test ids (`<prefix>-progress`, `<prefix>-marker-<n>`); the lesson's by default. */
  testIdPrefix?: string;
}

const KEY_MOVES: Record<string, (at: number, last: number) => number> = {
  ArrowRight: (at, last) => Math.min(at + 1, last),
  ArrowLeft: (at) => Math.max(at - 1, 0),
  Home: () => 0,
  End: (_, last) => last,
};

export function StepDots({
  steps,
  current,
  label,
  onSelect,
  onBlocked,
  testIdPrefix = "learn-lesson",
}: StepDotsProps): React.JSX.Element {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  return (
    <Row
      render="nav"
      aria-label={label}
      data-testid={`${testIdPrefix}-progress`}
      flexShrink={0}
      alignItems="stretch"
    >
      <Row render="ol" flex={1} gap={5} padding={0} margin={0} alignItems="stretch">
        {steps.map((step, i) => {
          const n = i + 1;
          const filled = n <= current;
          return (
            <Row key={`dot-${String(n)}`} render="li" flex={1} alignItems="stretch">
              {/* A raw button, not Pressable: a locked dot is aria-disabled, which Tamagui reads as
                  disabled and swallows the press for, yet it must still say why it will not move.
                  The target reaches 12px above and 28px below the 4px bar, so it never covers the
                  back button in the header and the bar keeps its size and place. */}
              <button
                ref={(node) => {
                  buttons.current[i] = node;
                }}
                type="button"
                className="ui-step-dot"
                style={{ cursor: step.reachable ? "pointer" : "default" }}
                aria-label={step.label}
                aria-current={n === current ? "step" : undefined}
                aria-disabled={step.reachable ? undefined : true}
                tabIndex={n === current ? 0 : -1}
                data-step={n}
                data-state={n < current ? "done" : n === current ? "current" : "todo"}
                data-testid={`${testIdPrefix}-marker-${String(n)}`}
                onClick={() => {
                  if (!step.reachable) onBlocked?.();
                  else if (n !== current) onSelect(n);
                }}
                onKeyDown={(event) => {
                  const move = KEY_MOVES[event.key];
                  if (move === undefined) return;
                  // Preventing the default keeps a surrounding pager's own arrow keys out.
                  event.preventDefault();
                  buttons.current[move(i, steps.length - 1)]?.focus();
                }}
              >
                <View
                  height={{ default: 4, a11yLight: 6, a11yDark: 6 }}
                  borderRadius={2}
                  borderWidth={{ default: 0, a11yLight: 2, a11yDark: 2 }}
                  borderColor={{ default: "lineStrong", a11yLight: "text", a11yDark: "text" }}
                  backgroundColor={
                    filled
                      ? { default: "accent", a11yLight: "text", a11yDark: "text" }
                      : { default: "surface3", a11yLight: "bg", a11yDark: "bg" }
                  }
                  boxShadow={n === current ? "0 0 0 1px var(--accentLine)" : "none"}
                  transitionProperty="background-color"
                  transitionDuration="var(--durBase)"
                />
              </button>
            </Row>
          );
        })}
      </Row>
    </Row>
  );
}
